import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

export type DSModalAction = {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
};

type Props = {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  actions: DSModalAction[];
  onClose?: () => void;
  size?: 'default' | 'compact';
  descriptionVariant?: 'default' | 'body16';
};

export default function DSModal({
  open,
  title,
  description,
  actions,
  onClose,
  size = 'default',
  descriptionVariant = 'default',
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      onPointerDown={() => {
        onClose?.();
      }}
    >
      <Dialog
        $size={size}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
      >
        <Title $size={size}>{title}</Title>
        {description ? <Description $size={size} $variant={descriptionVariant}>{description}</Description> : null}
        <Footer $count={actions.length} $size={size}>
          {actions.map((a, idx) => (
            <ActionButton
              key={`${a.label}-${idx}`}
              type="button"
              $size={size}
              $variant={a.variant ?? (idx === actions.length - 1 ? 'primary' : 'secondary')}
              onClick={a.onClick}
            >
              {a.label}
            </ActionButton>
          ))}
        </Footer>
      </Dialog>
    </Overlay>,
    document.body,
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
`;

const Dialog = styled.div<{ $size: 'default' | 'compact' }>`
  border-radius: 20px;
  background: #ffffff;
  box-shadow: ${({ $size }) =>
    $size === 'compact' ? '0px 0px 6px rgba(0, 0, 0, 0.35)' : '0 16px 36px rgba(0, 0, 0, 0.22)'};
  padding: ${({ $size }) => ($size === 'compact' ? '30px 20px 20px' : '28px 26px 22px')};
  min-width: 320px;
  max-width: ${({ $size }) => ($size === 'compact' ? 'min(100vw - 48px, 320px)' : 'none')};
  width: ${({ $size }) => ($size === 'compact' ? 'min(100vw - 48px, 320px)' : 'auto')};
`;

const Title = styled.div<{ $size: 'default' | 'compact' }>`
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 500;
  line-height: ${({ $size }) => ($size === 'compact' ? '150%' : '150%')};
  text-align: center;
  margin-bottom: ${({ $size }) => ($size === 'compact' ? '0' : '16px')};
`;

const Description = styled.div<{ $size: 'default' | 'compact'; $variant: 'default' | 'body16' }>`
  margin-top: ${({ $size }) => ($size === 'compact' ? '20px' : '12px')};
  color: #17171B;
  font-family: Pretendard;
  font-size: ${({ $variant, $size }) => ($variant === 'body16' ? '16px' : ($size === 'compact' ? '16px' : '14px'))};
  font-weight: 400;
  line-height: 150%;
  text-align: center;
  white-space: pre-line;
  margin-bottom: ${({ $size }) => ($size === 'compact' ? '0' : '16px')};
`;

const Footer = styled.div<{ $count: number; $size: 'default' | 'compact' }>`
  margin-top: ${({ $size }) => ($size === 'compact' ? '20px' : '18px')};
  display: grid;
  grid-template-columns: ${({ $count, $size }) =>
    $size === 'compact' && $count >= 2 ? 'repeat(2, 134px)' : ($count >= 2 ? '1fr 1fr' : '1fr')};
  justify-content: ${({ $size }) => ($size === 'compact' ? 'space-between' : 'stretch')};
  width: ${({ $size }) => ($size === 'compact' ? '100%' : 'auto')};
  gap: ${({ $size, $count }) => ($size === 'compact' && $count >= 2 ? '8px' : '12px')};
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'secondary'; $size: 'default' | 'compact' }>`
  height: 48px;
  border-radius: ${({ $size }) => ($size === 'compact' ? '12px' : '14px')};
  border: none;
  cursor: pointer;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: ${({ $size }) => ($size === 'compact' ? 400 : 500)};
  line-height: 150%;
  background: ${({ $variant, $size, theme }) =>
    $variant === 'primary'
      ? ($size === 'compact' ? theme.colors.primaryBlue[80] : theme.colors.primaryBlue[70])
      : $size === 'compact'
        ? '#DFDFDF'
        : theme.colors.grayScale[30]};
  color: ${({ $variant, theme }) => ($variant === 'primary' ? theme.colors.grayScale[0] : '#17171B')};
`;





